import type { FC, ReactNode } from 'react'
import type { Quote } from '@/types'

import { Truck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { CmsText } from '@/components/store/cms-text'
import { Price } from '@/components/store/price'

interface CartSummaryProps {
    quote: Quote
    showShipping?: boolean
    className?: string
}

export const FreeShippingMeter: FC<{ quote: Quote }> = ({ quote }): ReactNode => {
    const { txn, tx } = useI18n()
    const { totals, freeShippingEnabled, freeShippingThreshold } = quote

    if (!freeShippingEnabled || freeShippingThreshold <= 0) return null

    const progress = Math.min(100, Math.round((totals.merchandise / freeShippingThreshold) * 100))
    const reached = totals.amountToFreeShipping <= 0

    return (
        <div className='flex flex-col gap-2 rounded-2xl bg-navy-50/70 p-3.5'>
            <p className='flex items-center gap-2 text-xs text-navy-800'>
                <Truck className={cn('size-4 shrink-0', reached ? 'text-emerald-600' : 'text-cyan-700')} aria-hidden='true' />
                {reached
                    ? <span className='font-medium text-emerald-700'>{tx('طلبك مؤهل للشحن المجاني', 'Your order qualifies for free shipping')}</span>
                    : <span>{txn('أضف {amount} للحصول على شحن مجاني', 'Add {amount} more for free shipping', { amount: <strong className='font-semibold'><Price value={totals.amountToFreeShipping} /></strong> })}</span>}
            </p>

            <div className='h-1.5 overflow-hidden rounded-full bg-white' role='progressbar' aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
                <div className={cn('h-full rounded-full transition-all duration-500', reached ? 'bg-emerald-500' : 'bg-gradient-to-r from-cyan-400 to-cyan-600')} style={{ width: `${progress}%` }} />
            </div>
        </div>
    )
}

export const CartSummary: FC<CartSummaryProps> = ({ quote, showShipping = false, className }): ReactNode => {
    const { tx } = useI18n()
    const { totals } = quote

    const row = (label: ReactNode, value: ReactNode, emphasis = false): ReactNode => (
        <div className={cn('flex items-center justify-between gap-4', emphasis ? 'text-base font-semibold text-navy-950' : 'text-sm text-navy-700')}>
            <span>{label}</span>
            <span>{value}</span>
        </div>
    )

    return (
        <div className={cn('flex flex-col gap-2.5', className)}>
            {row(tx('المجموع الفرعي', 'Subtotal'), <Price value={totals.subtotal} />)}

            {totals.discount > 0 && row(
                tx('خصم العروض', 'Offer discount'),
                <span className='inline-flex items-center gap-1 font-medium text-emerald-700'>
                    <span aria-hidden='true'>-</span>
                    <Price value={totals.discount} />
                </span>
            )}

            {showShipping
                ? row(
                    quote.governorate
                        ? (
                            <span className='inline-flex flex-wrap items-center gap-1'>
                                {tx('الشحن إلى', 'Shipping to')}
                                {' '}
                                <CmsText table='governorates' id={quote.governorate.id} field={quote.governorate.name} className='font-medium text-navy-900' />
                            </span>
                        )
                        : tx('الشحن', 'Shipping'),
                    totals.shipping === null
                        ? <span className='text-xs text-muted-foreground'>{tx('اختر المحافظة', 'Choose governorate')}</span>
                        : totals.shipping === 0
                            ? <span className='font-medium text-emerald-700'>{tx('مجاني', 'Free')}</span>
                            : <Price value={totals.shipping} />
                )
                : row(tx('الشحن', 'Shipping'), <span className='text-xs text-muted-foreground'>{tx('يُحسب عند إتمام الطلب', 'Calculated at checkout')}</span>)}

            <div className='my-1 h-px bg-border' />

            {row(showShipping ? tx('الإجمالي', 'Total') : tx('إجمالي المنتجات', 'Items total'), <Price value={showShipping ? totals.total : totals.merchandise} />, true)}

            {totals.wholesaleSavings > 0 && (
                <p className='rounded-xl bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800 ring-1 ring-emerald-100'>
                    {tx('وفّرت', 'You saved')}
                    {' '}
                    <Price value={totals.wholesaleSavings} />
                    {' '}
                    {tx('بفضل أسعار الجملة', 'thanks to wholesale pricing')}
                </p>
            )}
        </div>
    )
}
